import type { BookingProjection, HotelBootstrap } from "../bridge/contract";

/**
 * What the guest has chosen so far.
 *
 * A booking funnel that hides the current selection makes people scroll back to check, and every
 * trip backwards is a chance to leave. On desktop this sits alongside the steps; on mobile it
 * collapses into a bar.
 *
 * Prices appear only once a quote exists. There is no running total before that, because there
 * is no number the server has stood behind yet — and inventing one here is the exact failure the
 * contract is shaped to prevent.
 */

function formatStay(checkin: string, checkout: string): string {
  const fmt = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString("de-DE", { day: "numeric", month: "short" });
  return `${fmt(checkin)} – ${fmt(checkout)}`;
}

export function BookingSummary({
  bootstrap,
  projection,
}: {
  bootstrap: HotelBootstrap;
  projection: BookingProjection;
}) {
  const { stay, rooms, addons, quote } = projection;
  const hasAnything = Boolean(stay) || rooms.some((room) => room.categoryCode);

  if (!hasAnything) {
    return (
      <aside className="summary summary--empty">
        <p className="muted">Ihre Auswahl erscheint hier, sobald Sie die Daten gewählt haben.</p>
      </aside>
    );
  }

  return (
    <aside className="summary" aria-label="Ihre Auswahl">
      {stay ? (
        <div className="summary-row">
          <span className="summary-label">Aufenthalt</span>
          <span className="summary-value">
            {formatStay(stay.checkin, stay.checkout)}
            <span className="muted"> · {stay.nights} {stay.nights === 1 ? "Nacht" : "Nächte"}</span>
          </span>
        </div>
      ) : null}

      {rooms.map((room, index) => {
        const category = bootstrap.categories.find((c) => c.code === room.categoryCode);
        const rate = bootstrap.rates.find((r) => r.code === room.rateProductCode);
        return (
          <div className="summary-row" key={room.roomId}>
            <span className="summary-label">{rooms.length > 1 ? `Zimmer ${index + 1}` : "Zimmer"}</span>
            <span className="summary-value">
              {category?.displayName ?? <span className="muted">noch offen</span>}
              <span className="muted"> · {room.partyLabel}</span>
              {rate ? <span className="muted"> · {rate.mealPlanLabel}</span> : null}
            </span>
          </div>
        );
      })}

      {addons.length > 0 ? (
        <div className="summary-row">
          <span className="summary-label">Extras</span>
          <span className="summary-value">{addons.map((a) => a.displayName).join(", ")}</span>
        </div>
      ) : null}

      {quote ? (
        <div className="summary-total">
          <div className="row-between total">
            <span>Gesamt</span>
            <strong>{quote.total.formatted}</strong>
          </div>
          <p className="muted">{quote.paymentSummary}</p>
        </div>
      ) : null}
    </aside>
  );
}
